<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { getInboxIconByType, INBOX_TYPES } from 'dashboard/helper/inbox';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import EmailCampaignDetails from './EmailCampaignDetails.vue';
import LiveChatCampaignDetails from './LiveChatCampaignDetails.vue';
import SMSCampaignDetails from './SMSCampaignDetails.vue';

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  message: {
    type: String,
    default: '',
  },
  isLiveChatType: {
    type: Boolean,
    default: false,
  },
  isEnabled: {
    type: Boolean,
    default: false,
  },
  status: {
    type: String,
    default: '',
  },
  sender: {
    type: Object,
    default: null,
  },
  inbox: {
    type: Object,
    default: null,
  },
  scheduledAt: {
    type: Number,
    default: 0,
  },
  deliverySummary: {
    type: Object,
    default: null,
  },
  deliveryReport: {
    type: Array,
    default: () => [],
  },
  attachmentsCount: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['edit', 'delete']);

const { t } = useI18n();

const STATUS_COMPLETED = 'completed';
const STATUS_PROCESSING = 'processing';

const { formatMessage } = useMessageFormatter();

const isActive = computed(() =>
  props.isLiveChatType ? props.isEnabled : props.status !== STATUS_COMPLETED
);

const statusTextColor = computed(() => ({
  'text-n-teal-11': isActive.value,
  'text-n-slate-12': !isActive.value,
}));

const inboxName = computed(() => props.inbox?.name || '');

const isEmailType = computed(
  () => props.inbox?.channel_type === INBOX_TYPES.EMAIL
);

const oneOffStatusText = status => {
  if (isEmailType.value) {
    if (status === 'COMPLETED')
      return t('CAMPAIGN.EMAIL.CARD.STATUS.COMPLETED');
    if (status === 'PROCESSING')
      return t('CAMPAIGN.EMAIL.CARD.STATUS.PROCESSING');
    return t('CAMPAIGN.EMAIL.CARD.STATUS.SCHEDULED');
  }

  if (status === 'COMPLETED') return t('CAMPAIGN.SMS.CARD.STATUS.COMPLETED');
  if (status === 'PROCESSING') return t('CAMPAIGN.SMS.CARD.STATUS.PROCESSING');
  return t('CAMPAIGN.SMS.CARD.STATUS.SCHEDULED');
};

const campaignStatus = computed(() => {
  if (props.isLiveChatType) {
    return props.isEnabled
      ? t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.ENABLED')
      : t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.DISABLED');
  }

  if (props.status === STATUS_COMPLETED) {
    return oneOffStatusText('COMPLETED');
  }

  if (props.status === STATUS_PROCESSING) {
    return oneOffStatusText('PROCESSING');
  }

  return oneOffStatusText('SCHEDULED');
});

const inboxIcon = computed(() => {
  const { medium, channel_type: type } = props.inbox || {};
  return getInboxIconByType(type, medium);
});

const deliveryReportItems = computed(
  () => props.deliveryReport?.slice(0, 5) ?? []
);

const hasDeliveryReport = computed(
  () => isEmailType.value && deliveryReportItems.value.length > 0
);

const deliveryContactLabel = delivery =>
  delivery.contact?.name || delivery.email || delivery.source_id || '-';

const deliveryStatusLabel = status => {
  const normalizedStatus = status?.toUpperCase() || 'PENDING';
  if (normalizedStatus === 'PROCESSING') {
    return t('CAMPAIGN.EMAIL.CARD.REPORT.STATUS.PROCESSING');
  }
  if (normalizedStatus === 'SENT') {
    return t('CAMPAIGN.EMAIL.CARD.REPORT.STATUS.SENT');
  }
  if (normalizedStatus === 'SENDING') {
    return t('CAMPAIGN.EMAIL.CARD.REPORT.STATUS.SENDING');
  }
  if (normalizedStatus === 'DELIVERY_UNKNOWN') {
    return t('CAMPAIGN.EMAIL.CARD.REPORT.STATUS.DELIVERY_UNKNOWN');
  }
  if (normalizedStatus === 'SKIPPED') {
    return t('CAMPAIGN.EMAIL.CARD.REPORT.STATUS.SKIPPED');
  }
  if (normalizedStatus === 'FAILED') {
    return t('CAMPAIGN.EMAIL.CARD.REPORT.STATUS.FAILED');
  }
  return t('CAMPAIGN.EMAIL.CARD.REPORT.STATUS.PENDING');
};
</script>

<template>
  <CardLayout layout="row">
    <div class="flex flex-col items-start justify-between flex-1 min-w-0 gap-2">
      <div class="flex justify-between gap-3 w-fit">
        <span
          class="text-base font-medium capitalize text-n-slate-12 line-clamp-1"
        >
          {{ title }}
        </span>
        <span
          class="text-xs font-medium inline-flex items-center h-6 px-2 py-0.5 rounded-md bg-n-alpha-2"
          :class="statusTextColor"
        >
          {{ campaignStatus }}
        </span>
      </div>
      <div
        v-dompurify-html="formatMessage(message, false, false, false)"
        class="text-sm text-n-slate-11 line-clamp-1 [&>p]:mb-0 h-6"
      />
      <div class="flex items-center w-full min-h-6 gap-2 overflow-hidden">
        <LiveChatCampaignDetails
          v-if="isLiveChatType"
          :sender="sender"
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
        />
        <SMSCampaignDetails
          v-else-if="!isEmailType"
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
          :scheduled-at="scheduledAt"
        />
        <EmailCampaignDetails
          v-else
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
          :scheduled-at="scheduledAt"
          :delivery-summary="deliverySummary"
          :attachments-count="attachmentsCount"
        />
      </div>
    </div>
    <div class="flex items-center justify-end w-20 gap-2">
      <Button
        v-if="isLiveChatType"
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-sliders-vertical"
        @click="emit('edit')"
      />
      <Button
        variant="faded"
        color="ruby"
        size="sm"
        icon="i-lucide-trash"
        @click="emit('delete')"
      />
    </div>
    <template #after>
      <div
        v-if="hasDeliveryReport"
        class="px-6 pt-3 pb-5 border-t border-n-container"
      >
        <div class="flex items-center justify-between gap-3 mb-2">
          <span class="text-xs font-medium uppercase text-n-slate-11">
            {{ t('CAMPAIGN.EMAIL.CARD.REPORT.TITLE') }}
          </span>
          <span class="text-xs text-n-slate-10">
            {{
              t('CAMPAIGN.EMAIL.CARD.REPORT.TOTAL', {
                count: deliverySummary?.total || deliveryReportItems.length,
              })
            }}
          </span>
        </div>
        <div class="flex flex-col divide-y divide-n-weak">
          <div
            v-for="delivery in deliveryReportItems"
            :key="delivery.id"
            class="grid items-center grid-cols-[minmax(0,1fr)_auto_auto_auto] gap-3 py-2 text-xs"
          >
            <span class="font-medium truncate text-n-slate-12">
              {{ deliveryContactLabel(delivery) }}
            </span>
            <span class="px-2 py-0.5 rounded-md bg-n-alpha-2 text-n-slate-11">
              {{ deliveryStatusLabel(delivery.status) }}
            </span>
            <span class="text-n-slate-10 whitespace-nowrap">
              {{
                t('CAMPAIGN.EMAIL.CARD.REPORT.OPENED', {
                  count: delivery.open_count || 0,
                })
              }}
            </span>
            <span class="text-n-slate-10 whitespace-nowrap">
              {{
                t('CAMPAIGN.EMAIL.CARD.REPORT.CLICKED', {
                  count: delivery.click_count || 0,
                })
              }}
            </span>
          </div>
        </div>
      </div>
    </template>
  </CardLayout>
</template>
